/**
 * Console UI atoms — flat "pencil/graphite" primitives built exclusively
 * on the console semantic tokens (``theme/console.css``).
 *
 * Why not the @ando/ui Button/Badge directly: those primitives hard-code
 * light-only classes (gradient orange, ``text-ink-*``, rose-*) that break
 * in the graphite theme and violate the console's flat/no-gradient
 * mandate. Behavioral primitives (Radix Dialog) ARE reused — only the
 * cosmetic layer is local. ZERO raw hex here: every color goes through
 * ``var(--con-*)``.
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import {
  Check,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  Copy,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import * as React from "react";

import { Select, SelectContent, SelectItem, SelectTrigger, cn } from "@ando/ui";
import { useConsoleThemeClass } from "../theme/ConsoleTheme";

// ── Focus ring (shared) ───────────────────────────────────────────────────

export const conFocusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--con-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--con-surface)]";

// ── Button ────────────────────────────────────────────────────────────────

type ConButtonVariant = "primary" | "outline" | "ghost" | "danger";
type ConButtonSize = "sm" | "md";

const BUTTON_VARIANT_CLASSES: Record<ConButtonVariant, string> = {
  // Primary CTA — SOLID flat orange (no gradient), ink-on-orange for AA.
  primary:
    "bg-[color:var(--con-accent)] text-[color:var(--con-on-accent)] font-semibold hover:bg-[color:var(--con-accent-hover)] disabled:hover:bg-[color:var(--con-accent)]",
  outline:
    "border border-[color:var(--con-border-strong)] bg-[color:var(--con-surface-raised)] text-[color:var(--con-on-surface)] hover:bg-[color:var(--con-surface-hover)]",
  ghost:
    "bg-transparent text-[color:var(--con-on-surface-muted)] hover:bg-[color:var(--con-surface-hover)] hover:text-[color:var(--con-on-surface)]",
  danger:
    "border border-[color:var(--con-danger)] bg-transparent text-[color:var(--con-danger-text)] hover:bg-[color:var(--con-danger-soft)]",
};

const BUTTON_SIZE_CLASSES: Record<ConButtonSize, string> = {
  sm: "h-7 px-2.5 text-[length:var(--con-text-xs)] gap-1.5 [&_svg]:size-3",
  md: "h-8 px-3 text-[length:var(--con-text-sm)] gap-2 [&_svg]:size-3.5",
};

/** Shared button class recipe — reused by ConButton AND ConLinkButton, so a
 *  link-that-is-a-button is pixel-identical to a real button (DRY: kills the
 *  hand-rolled "link styled like a button" that drifted across pages). */
function conButtonClasses(
  variant: ConButtonVariant,
  size: ConButtonSize,
  className?: string,
): string {
  return cn(
    "con-control con-press inline-flex select-none items-center justify-center rounded-[8px] font-medium",
    "transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    conFocusRing,
    BUTTON_VARIANT_CLASSES[variant],
    BUTTON_SIZE_CLASSES[size],
    className,
  );
}

export interface ConButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ConButtonVariant;
  size?: ConButtonSize;
  /** Show a spinner, set aria-busy, and disable — for a pending async action. */
  loading?: boolean;
}

export const ConButton = React.forwardRef<HTMLButtonElement, ConButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      type,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type ?? "button"}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={conButtonClasses(variant, size, className)}
      {...props}
    >
      {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
      {children}
    </button>
  ),
);

/** A router `<Link>` styled exactly as a ConButton — the ONE primitive for
 *  button-shaped links (replaces the 4+ hand-rolled variants the audit found).
 *  Uses the non-generic `ComponentProps<Link>`, so PARAMLESS routes are fully
 *  typed; the rare param route passes `params` through a localized cast at the
 *  call site (createLink would type params cleanly but breaks every unit test
 *  that mocks the router without it — not worth it for one link). */
export function ConLinkButton({
  variant = "primary",
  size = "md",
  className,
  ...props
}: {
  variant?: ConButtonVariant;
  size?: ConButtonSize;
} & React.ComponentProps<typeof Link>): React.ReactElement {
  return <Link className={conButtonClasses(variant, size, className)} {...props} />;
}
ConButton.displayName = "ConButton";

// ── Badge ─────────────────────────────────────────────────────────────────

export type ConBadgeTone = "neutral" | "accent" | "success" | "warning" | "danger";

const BADGE_TONE_CLASSES: Record<ConBadgeTone, string> = {
  neutral:
    "bg-[color:var(--con-surface-sunken)] text-[color:var(--con-on-surface-muted)] border-[color:var(--con-border)]",
  accent:
    "bg-[color:var(--con-accent-soft)] text-[color:var(--con-accent-text)] border-transparent",
  success:
    "bg-[color:var(--con-success-soft)] text-[color:var(--con-success-text)] border-transparent",
  warning:
    "bg-[color:var(--con-warning-soft)] text-[color:var(--con-warning-text)] border-transparent",
  danger:
    "bg-[color:var(--con-danger-soft)] text-[color:var(--con-danger-text)] border-transparent",
};

export function ConBadge({
  tone = "neutral",
  className,
  children,
}: {
  tone?: ConBadgeTone;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 font-medium text-[length:var(--con-text-2xs)] leading-4",
        BADGE_TONE_CLASSES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────

export function ConCard({
  className,
  children,
  interactive = false,
}: {
  className?: string;
  children: React.ReactNode;
  /** Adds hover/focus lift only when the whole panel is actionable. */
  interactive?: boolean;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "con-panel rounded-[16px] border border-[color:var(--con-border)] bg-[color:var(--con-surface-raised)]",
        interactive && "con-panel-interactive",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ConCardHeader({
  title,
  description,
  actions,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-3 border-[color:var(--con-border)] border-b px-4 py-3">
      <div className="min-w-0">
        <h2 className="font-display font-semibold text-[length:var(--con-text-md)] text-[color:var(--con-on-surface)]">
          {title}
        </h2>
        {description ? (
          <p className="mt-0.5 text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-faint)]">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}

// ── Form controls ─────────────────────────────────────────────────────────

export const conFieldClass = cn(
  "con-field w-full rounded-[8px] border border-[color:var(--con-border-strong)] bg-[color:var(--con-surface-raised)]",
  "px-2.5 py-1.5 text-[length:var(--con-text-sm)] text-[color:var(--con-on-surface)]",
  "placeholder:text-[color:var(--con-on-surface-faint)]",
  "transition-colors duration-150 ease-out",
  "focus:border-[color:var(--con-accent)] focus:outline-none focus:ring-1 focus:ring-[color:var(--con-focus)]",
  "disabled:pointer-events-none disabled:opacity-50",
);

/** Danger border + focus ring for an invalid field (paired with aria-invalid). */
const conInvalidClass =
  "border-[color:var(--con-danger)] focus:border-[color:var(--con-danger)] focus:ring-[color:var(--con-danger)]";

export const ConInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(({ className, invalid, ...props }, ref) => (
  <input
    ref={ref}
    aria-invalid={invalid || undefined}
    className={cn(conFieldClass, invalid && conInvalidClass, className)}
    {...props}
  />
));
ConInput.displayName = "ConInput";

export const ConTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(({ className, invalid, ...props }, ref) => (
  <textarea
    ref={ref}
    aria-invalid={invalid || undefined}
    className={cn(conFieldClass, "min-h-20 resize-y", invalid && conInvalidClass, className)}
    {...props}
  />
));
ConTextarea.displayName = "ConTextarea";

type ConSelectProps = React.AriaAttributes & {
  className?: string;
  value?: string | number | readonly string[];
  defaultValue?: string | number | readonly string[];
  disabled?: boolean;
  id?: string;
  name?: string;
  children?: React.ReactNode;
  onChange?: (event: { target: { value: string } }) => void;
};

/** Console-skinned adapter over the shared Radix Select.
 *
 * Existing call sites intentionally keep their native-select-shaped contract;
 * the rendered control is never an OS `<select>`. This removes native chrome
 * from the whole Console at one boundary instead of restyling every page.
 */
export function ConSelect({
  className,
  value,
  defaultValue,
  disabled,
  id,
  name,
  onChange,
  children,
  ...ariaProps
}: ConSelectProps): React.ReactElement {
  // Radix portals the menu to document.body. Reapply the Console scope there:
  // without it --con-* colors are undefined and the menu is transparent,
  // allowing the sync button and account details to show through its rows.
  const themeClass = useConsoleThemeClass();
  const emptyValue = "__console_select_empty__";
  const normalise = (candidate: string | number | readonly string[] | undefined) =>
    candidate === "" ? emptyValue : candidate === undefined ? undefined : String(candidate);
  const options = React.Children.toArray(children).filter(React.isValidElement);
  return (
    <Select
      value={normalise(value)}
      defaultValue={normalise(defaultValue)}
      disabled={disabled}
      name={name}
      onValueChange={(next) => onChange?.({ target: { value: next === emptyValue ? "" : next } })}
    >
      <SelectTrigger
        id={id}
        className={cn(
          conFieldClass,
          "bg-[color:var(--con-surface-raised)] text-[color:var(--con-on-surface)] hover:bg-[color:var(--con-surface-hover)]",
          className,
        )}
        {...ariaProps}
      />
      <SelectContent className={cn(themeClass, "border-[color:var(--con-border)] bg-[color:var(--con-surface-raised)] text-[color:var(--con-on-surface)]")}>
        {options.map((option, index) => {
          const props = option.props as React.OptionHTMLAttributes<HTMLOptionElement>;
          return (
            <SelectItem
              key={String(props.value ?? index)}
              value={normalise(props.value) ?? emptyValue}
              disabled={props.disabled}
              className="focus:bg-[color:var(--con-surface-hover)] focus:text-[color:var(--con-on-surface)]"
            >
              {props.children}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

export function ConLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1 block font-medium text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-muted)]"
    >
      {children}
    </label>
  );
}

export function ConFieldGroup({
  title,
  description,
  children,
  className,
}: {
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <section
      className={cn(
        "con-panel con-panel-soft rounded-[16px] border border-[color:var(--con-border)] bg-[color:var(--con-surface-sunken)]/45 p-3.5",
        className,
      )}
    >
      {title ? (
        <div className="mb-3">
          <h3 className="font-semibold text-[color:var(--con-on-surface)] text-[length:var(--con-text-sm)]">
            {title}
          </h3>
          {description ? (
            <p className="mt-0.5 max-w-2xl text-[color:var(--con-on-surface-faint)] text-[length:var(--con-text-xs)]">
              {description}
            </p>
          ) : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function ConFieldStatus({
  children,
  tone = "neutral",
  pulse = false,
}: {
  children: React.ReactNode;
  tone?: "neutral" | "success" | "danger";
  /** Motion is opt-in and reserved for a state just caused by user/system work. */
  pulse?: boolean;
}): React.ReactElement {
  const Icon = tone === "success" ? CircleCheck : tone === "danger" ? CircleAlert : null;
  return (
    <p
      role={tone === "danger" ? "alert" : "status"}
      className={cn(
        "mt-1.5 flex items-start gap-1.5 text-[length:var(--con-text-xs)]",
        tone === "success" && "text-[color:var(--con-success-text)]",
        tone === "danger" && "text-[color:var(--con-danger-text)]",
        tone === "neutral" && "text-[color:var(--con-on-surface-faint)]",
        pulse && tone !== "neutral" && "con-causal-state",
      )}
    >
      {Icon ? <Icon aria-hidden className="mt-px size-3.5 shrink-0" strokeWidth={1.8} /> : null}
      <span>{children}</span>
    </p>
  );
}

// ── Tab picker (shared segmented control) ───────────────────────────────────
// One flat hairline tablist reused wherever the console lets you pick ONE of a
// small set (structured source, connector, …) — replaces the near-identical
// per-page pickers.

export interface ConTabPickerOption {
  id: string;
  label: string;
  icon?: LucideIcon;
}

export function ConTabPicker({
  options,
  activeId,
  onSelect,
  ariaLabel,
}: {
  options: readonly ConTabPickerOption[];
  activeId: string | null;
  onSelect: (id: string) => void;
  ariaLabel: string;
}): React.ReactElement {
  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = opt.id === activeId;
        const Icon = opt.icon;
        return (
          <button
            key={opt.id}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(opt.id)}
            className={cn(
              "inline-flex h-8 items-center gap-2 rounded-[8px] border px-2.5 font-medium text-[length:var(--con-text-xs)]",
              "transition-colors duration-150 ease-out",
              conFocusRing,
              active
                ? "border-[color:var(--con-accent)] bg-[color:var(--con-accent-soft)] text-[color:var(--con-on-surface)]"
                : "border-[color:var(--con-border)] text-[color:var(--con-on-surface-muted)] hover:bg-[color:var(--con-surface-hover)]",
            )}
          >
            {Icon ? <Icon aria-hidden strokeWidth={1.75} className="size-3.5" /> : null}
            <span className="max-w-48 truncate">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Copy-to-clipboard (check feedback) ────────────────────────────────────

export function ConCopyButton({
  value,
  label,
  children,
  className,
}: {
  /** The text written to the clipboard. */
  value: string;
  /** Accessible name; defaults to a generic "Copia". */
  label?: string;
  /** Optional visible text next to the icon (e.g. <Trans>Copia</Trans>). */
  children?: React.ReactNode;
  className?: string;
}): React.ReactElement {
  const { i18n } = useLingui();
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | null>(null);

  React.useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = (): void => {
    void navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        if (timer.current !== null) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard unavailable (permissions / Tauri edge) — no-op.
      });
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={label ?? i18n._(msg`Copia`)}
      title={label ?? i18n._(msg`Copia`)}
      className={cn(
        "con-press inline-flex h-6 shrink-0 items-center gap-1 rounded-sm px-1.5 font-medium text-[length:var(--con-text-xs)]",
        "transition-colors duration-150 ease-out",
        conFocusRing,
        copied
          ? "text-[color:var(--con-success-text)]"
          : "text-[color:var(--con-on-surface-faint)] hover:bg-[color:var(--con-surface-hover)] hover:text-[color:var(--con-on-surface)]",
        className,
      )}
    >
      {copied ? (
        <Check size={12} strokeWidth={2} aria-hidden />
      ) : (
        <Copy size={12} strokeWidth={1.75} aria-hidden />
      )}
      {children}
    </button>
  );
}

// ── Table pieces ──────────────────────────────────────────────────────────

export const conThClass =
  "px-4 py-2 text-left font-semibold text-[length:var(--con-text-2xs)] uppercase tracking-[0.08em] text-[color:var(--con-on-surface-faint)] whitespace-nowrap";

export const conTdClass =
  "px-4 py-2.5 text-[length:var(--con-text-sm)] text-[color:var(--con-on-surface)] align-middle";

/** Right-aligned header cell for numeric columns. */
export const conThNumClass = cn(conThClass, "text-right");

/** Right-aligned numeric cell — tabular figures for column rhythm. */
export const conTdNumClass = cn(conTdClass, "text-right tabular-nums");

export function ConTable({
  children,
  ariaLabel,
}: {
  children: React.ReactNode;
  ariaLabel?: string;
}): React.ReactElement {
  return (
    <div className="con-table-frame overflow-x-auto rounded-[12px]">
      <table aria-label={ariaLabel} className="con-table w-full border-collapse">
        {children}
      </table>
    </div>
  );
}

export function ConTHead({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    // Sticky: inert on short tables, keeps the header pinned inside any
    // scrolling wrapper (e.g. the max-h daily-breakdown list). The sunken
    // surface is opaque in both themes, so rows never bleed through.
    <thead className="sticky top-0 z-[1] border-[color:var(--con-border)] border-b bg-[color:var(--con-surface-sunken)]">
      {children}
    </thead>
  );
}

export function ConTr({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <tr
      className={cn(
        // `group` lets sticky (opaque) cells mirror the row hover via
        // `group-hover:` — otherwise a frozen first/last column stays unlit.
        "group border-[color:var(--con-border)] border-b last:border-b-0",
        "transition-colors duration-150 ease-out hover:bg-[color:var(--con-surface-hover)]",
        className,
      )}
    >
      {children}
    </tr>
  );
}

// ── States ────────────────────────────────────────────────────────────────

export function ConEmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  /** Optional leading glyph — a quiet Lucide icon that makes the state
   * scannable at a glance (SOTA empty-state pattern), never color-only. */
  icon?: LucideIcon;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      {Icon ? (
        <span
          aria-hidden
          className="mb-1 flex size-11 items-center justify-center rounded-full bg-[color:var(--con-surface-sunken)] text-[color:var(--con-on-surface-faint)]"
        >
          <Icon size={19} strokeWidth={1.5} />
        </span>
      ) : null}
      <p className="font-medium text-[length:var(--con-text-md)] text-[color:var(--con-on-surface)]">
        {title}
      </p>
      {description ? (
        <p className="max-w-md text-[length:var(--con-text-xs)] text-[color:var(--con-on-surface-faint)]">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ConSkeleton({ className }: { className?: string }): React.ReactElement {
  return (
    <div
      aria-hidden
      className={cn("con-shimmer rounded-sm bg-[color:var(--con-surface-sunken)]", className)}
    />
  );
}

/** Stable placeholder ids — skeleton rows are homogeneous and never reorder. */
const SKELETON_SLOT_IDS = [
  "s1",
  "s2",
  "s3",
  "s4",
  "s5",
  "s6",
  "s7",
  "s8",
  "s9",
  "s10",
  "s11",
  "s12",
] as const;

export function ConSkeletonRows({ rows = 4 }: { rows?: number }): React.ReactElement {
  return (
    <div className="flex flex-col gap-2 p-4">
      {SKELETON_SLOT_IDS.slice(0, Math.min(rows, SKELETON_SLOT_IDS.length)).map((id) => (
        <ConSkeleton key={id} className="h-8 w-full" />
      ))}
    </div>
  );
}

/** The ONE KPI tile — unifies the three per-page metric tiles (24/20/13px)
 *  onto a single size (--con-text-2xl) so Overview / Usage / detail agree. */
export function ConStat({
  icon: Icon,
  label,
  value,
  hint,
  size = "default",
  interactive = false,
}: {
  icon?: LucideIcon;
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  /** ``sm`` shrinks the hero value to ``--con-text-md`` — reserve the 28px
   *  hero size for NUMBERS; use ``sm`` for text/datetime/categorical values. */
  size?: "default" | "sm";
  interactive?: boolean;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "con-panel rounded-[16px] border border-[color:var(--con-border)] bg-[color:var(--con-surface-raised)] p-4",
        interactive && "con-panel-interactive",
      )}
    >
      <div className="flex items-center gap-2 text-[color:var(--con-on-surface-faint)]">
        {Icon ? <Icon aria-hidden strokeWidth={1.75} className="size-3.5" /> : null}
        <span className="font-medium text-[length:var(--con-text-2xs)] uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div
        className={cn(
          "mt-2 font-display font-semibold text-[color:var(--con-on-surface)] tabular-nums",
          size === "sm" ? "text-[length:var(--con-text-md)]" : "text-[length:var(--con-text-2xl)]",
        )}
      >
        {value}
      </div>
      {hint ? (
        <div className="mt-0.5 text-[length:var(--con-text-2xs)] text-[color:var(--con-on-surface-faint)] tabular-nums">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

export function ConErrorNote({ message }: { message: string }): React.ReactElement {
  return (
    <p
      role="alert"
      className="rounded-sm border border-[color:var(--con-danger)] bg-[color:var(--con-danger-soft)] px-3 py-2 text-[length:var(--con-text-xs)] text-[color:var(--con-danger-text)]"
    >
      {message}
    </p>
  );
}

// ── Breadcrumbs (detail-view orientation) ─────────────────────────────────

export interface ConCrumb {
  label: React.ReactNode;
  /** When present (and not the last crumb), the crumb is a router Link. */
  to?: string;
  params?: Record<string, string>;
}

/**
 * Breadcrumb trail for deep-dive routes — the SOTA orientation pattern
 * (Stripe / Vercel / Supabase): where am I, and one click back up each
 * level. Neutral ink, a faint chevron separator, the current page in
 * on-surface weight. The single orange accent is NOT spent here.
 */
export function ConBreadcrumbs({ items }: { items: ConCrumb[] }): React.ReactElement {
  const { i18n } = useLingui();
  return (
    <nav aria-label={i18n._(msg`Percorso di navigazione`)} className="mb-1.5 min-w-0">
      <ol className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-[length:var(--con-text-xs)]">
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <li key={item.to ?? `crumb-${idx}`} className="flex min-w-0 items-center gap-1">
              {idx > 0 ? (
                <ChevronRight
                  aria-hidden
                  size={12}
                  strokeWidth={1.75}
                  className="shrink-0 text-[color:var(--con-on-surface-faint)]"
                />
              ) : null}
              {item.to && !isLast ? (
                <Link
                  to={item.to as never}
                  params={item.params as never}
                  className={cn(
                    "truncate rounded-sm text-[color:var(--con-on-surface-muted)]",
                    "transition-colors duration-150 ease-out hover:text-[color:var(--con-on-surface)]",
                    conFocusRing,
                  )}
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? "page" : undefined}
                  className={cn(
                    "truncate",
                    isLast
                      ? "font-medium text-[color:var(--con-on-surface)]"
                      : "text-[color:var(--con-on-surface-muted)]",
                  )}
                >
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// ── Keyboard hint ─────────────────────────────────────────────────────────

/**
 * ``ConKbd`` — a keyboard-shortcut hint. HAIRLINE contour only (never a
 * filled pill): a 1px border on the sunken surface, mono figures. Used for
 * the ⌘K / ⌘I hints in the command palette and the MCP rows.
 */
export function ConKbd({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <kbd
      className={cn(
        "inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[4px] px-1",
        "border border-[color:var(--con-border)] bg-[color:var(--con-surface-sunken)]",
        "font-mono text-[length:var(--con-text-2xs)] text-[color:var(--con-on-surface-faint)] leading-none",
        className,
      )}
    >
      {children}
    </kbd>
  );
}

// ── Page scaffolding ──────────────────────────────────────────────────────

export function ConPage({
  breadcrumbs,
  title,
  description,
  actions,
  children,
  wide = false,
  prose = false,
  compact = false,
}: {
  /** Optional breadcrumb trail, rendered above the title on detail views. */
  breadcrumbs?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Extra-wide content column for the densest data surfaces (a two-pane map,
   * a wide table): ``max-w-[88rem]`` (~1408px). The DEFAULT is already generous
   * — ``max-w-7xl`` (~1280px), the SOTA cap for a developer console (roomy for
   * tables/grids, still under the ~75ch readability ceiling; prose blocks carry
   * their own narrower measure). ``wide`` only adds the top-end for data-dense
   * dashboards; reading-first pages should stay on the default.
   */
  wide?: boolean;
  /** Reading-first pages (docs): narrower ``max-w-5xl`` measure. Wins over ``wide``. */
  prose?: boolean;
  /** Dense operational pages use a quieter page heading. */
  compact?: boolean;
}): React.ReactElement {
  return (
    <div
      className={`con-page-enter mx-auto flex w-full flex-col gap-4 px-4 py-5 md:px-6 ${
        prose ? "max-w-5xl" : wide ? "max-w-[88rem]" : "max-w-7xl"
      }`}
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {breadcrumbs}
          <h1
            className={`font-display font-semibold text-[color:var(--con-on-surface)] tracking-[-0.015em] ${compact ? "text-[length:var(--con-text-lg)]" : "text-[length:var(--con-text-xl)]"}`}
          >
            {title}
          </h1>
          {description ? (
            <p className="mt-1 max-w-2xl text-[length:var(--con-text-sm)] text-[color:var(--con-on-surface-muted)]">
              {description}
            </p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </header>
      {children}
    </div>
  );
}
